import { useCollection, useDocument } from 'react-firebase-hooks/firestore';
import { collection, doc, query, where } from 'firebase/firestore';
import { auth, firestore } from '../firebase/firebase';
import { constructUserQuestionsWithAnswers } from '../utils/dataHelpers';
import { useAuthState } from 'react-firebase-hooks/auth';

export const useUserData = (userArg) => {
  const [authUser] = useAuthState(auth);
  const user = userArg || authUser;

  const userEmail = user?.email;
  const userUid = user?.uid;

  const [userDataByUid, loadingUserDataByUid] = useDocument(
    // Safety check to prevent data mixing
    userUid ? doc(firestore, 'users', userUid) : null,
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  // Some users are stored in the Firebase collection by their email address
  const [userDataByEmail, loadingUserDataByEmail] = useDocument(
    // Safety check to prevent data mixing
    userEmail ? doc(firestore, 'users', userEmail) : null,
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const userData = userDataByUid?.data() || userDataByEmail?.data();

  const emailRepliesQuery = userEmail
    ? query(
        collection(firestore, 'email-replies'),
        where('sender', '==', userEmail)
      )
    : null;

  const [emailReplies, emailRepliesLoading] = useCollection(emailRepliesQuery, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });

  const [questions, questionsLoading] = useCollection(
    collection(firestore, 'questions'),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const userLanguage = userData?.language || 'en';

  // Critical safety check: Only construct data if we have valid user context
  // and the emailReplies data matches the current user
  const userQuestionsWithAnswers =
    userEmail && emailReplies && !emailRepliesLoading
      ? constructUserQuestionsWithAnswers({
          questions,
          answers: emailReplies,
          userEmail,
          language: userLanguage,
          bookQaPhotos: userData?.book_qa_photos,
        })
      : null;

  return {
    userData,
    userQuestionsWithAnswers,
    emailRepliesLoading,
    loading:
      emailRepliesLoading ||
      questionsLoading ||
      loadingUserDataByUid ||
      loadingUserDataByEmail,
    userLanguage,
    userEmail,
    userUid,
  };
};
